import { FormEvent, useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { TariffTier, errorMessage, operatorApi, priceForQuantity, useFetch } from '../../services/operatorApi';
import { Button, Card, Empty, ErrorBox, Field, PageHeader, Spinner, Table, Td, dateTime, eur, inputClass } from '../../components/ui';

interface Settings {
  tariff: { tiers: TariffTier[]; trial_credits: number };
  costs: { sms_cents: number; signature_cents: number };
}

interface AuditEntry {
  action: string;
  target_type: string | null;
  target_id: string | null;
  detail: Record<string, unknown>;
  ip_address: string | null;
  created_at: string;
}

const ACTIONS: Record<string, string> = {
  'operator.login': 'Connexion',
  'operator.login_failed': 'Connexion refusée',
  'client.created': 'Client créé',
  'client.updated': 'Client modifié',
  'key.created': 'Clé API créée',
  'key.revoked': 'Clé API révoquée',
  'credits.purchase': 'Lot vendu',
  'credits.grant': 'Signatures offertes',
  'credits.adjustment': 'Correction de crédits',
  'settings.updated': 'Réglages modifiés',
  'document.reseal': 'Scellement relancé',
  'suppression.removed': 'Adresse réautorisée',
};

export default function SettingsPage() {
  const { data, error, loading, reload } = useFetch(async () => (await operatorApi.get<Settings>('/settings')).data);
  const audit = useFetch(async () => (await operatorApi.get<{ entries: AuditEntry[] }>('/audit-log')).data.entries);

  const [tiers, setTiers] = useState<{ up_to: string; unit: string }[]>([]);
  const [trial, setTrial] = useState('0');
  const [sms, setSms] = useState('0');
  const [signature, setSignature] = useState('0');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    if (!data) return;
    setTiers(data.tariff.tiers.map((t) => ({ up_to: t.up_to === null ? '' : String(t.up_to), unit: (t.unit_cents / 100).toFixed(2) })));
    setTrial(String(data.tariff.trial_credits));
    setSms(String(data.costs.sms_cents / 100));
    setSignature(String(data.costs.signature_cents / 100));
  }, [data]);

  if (loading && !data) return <Spinner />;
  if (error && !data) return <ErrorBox message={error} onRetry={reload} />;

  const toNumber = (value: string) => Number(value.replace(',', '.'));
  const parsed: TariffTier[] = tiers.map((t, index) => ({
    up_to: index === tiers.length - 1 ? null : Math.floor(Number(t.up_to) || 0),
    unit_cents: Math.round(toNumber(t.unit) * 100) || 0,
  }));

  const update = (index: number, field: 'up_to' | 'unit', value: string) =>
    setTiers((current) => current.map((t, i) => (i === index ? { ...t, [field]: value } : t)));

  const addTier = () =>
    setTiers((current) => {
      const last = current[current.length - 1];
      const previousLimit = current.length > 1 ? Number(current[current.length - 2].up_to) || 0 : 0;
      return [...current.slice(0, -1), { up_to: String(previousLimit + 50), unit: last.unit }, { up_to: '', unit: last.unit }];
    });

  const save = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      await operatorApi.put('/settings', {
        tariff: { tiers: parsed, trial_credits: Number(trial) || 0 },
        costs: { sms_cents: Math.round(toNumber(sms) * 10000) / 100, signature_cents: Math.round(toNumber(signature) * 10000) / 100 },
      });
      setMessage({ ok: true, text: 'Réglages enregistrés. Ils s’appliquent aux prochaines ventes.' });
      await reload();
      await audit.reload();
    } catch (e) {
      setMessage({ ok: false, text: errorMessage(e) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader title="Réglages" subtitle="Tarifs des lots, essai gratuit et coûts estimés" />

      <form onSubmit={save} className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Card title="Tarif des lots (par signature)">
          <p className="mb-4 text-sm text-slate-600">
            Tarif au volume : tout le lot est facturé au prix de sa tranche. Avec 1,50 € jusqu&apos;à 49 signatures puis 0,99 € dès 50, un lot de 49 coûte
            {' '}<strong>{eur(priceForQuantity([{ up_to: 49, unit_cents: 150 }, { up_to: null, unit_cents: 99 }], 49))}</strong> et un lot de 50 coûte{' '}
            <strong>{eur(priceForQuantity([{ up_to: 49, unit_cents: 150 }, { up_to: null, unit_cents: 99 }], 50))}</strong>.
          </p>
          <div className="space-y-3">
            {tiers.map((tier, index) => {
              const last = index === tiers.length - 1;
              const from = index === 0 ? 1 : (Number(tiers[index - 1].up_to) || 0) + 1;
              return (
                <div key={index} className="flex items-end gap-3">
                  <Field label={last ? `À partir de ${from}` : `De ${from} jusqu'à`}>
                    <input className={inputClass} type="number" min={from} value={last ? '' : tier.up_to} disabled={last} placeholder={last ? 'sans limite' : ''} onChange={(e) => update(index, 'up_to', e.target.value)} />
                  </Field>
                  <Field label="Prix unitaire (€)">
                    <input className={inputClass} inputMode="decimal" value={tier.unit} onChange={(e) => update(index, 'unit', e.target.value)} required />
                  </Field>
                  <div className="pb-1">
                    {tiers.length > 1 && (
                      <button type="button" className="rounded p-2 text-slate-400 hover:bg-red-50 hover:text-red-600" aria-label="Supprimer la tranche" onClick={() => setTiers((current) => current.filter((_, i) => i !== index))}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-3">
            <Button variant="secondary" small onClick={addTier}><Plus className="w-4 h-4" />Ajouter une tranche</Button>
          </div>
          <div className="mt-5 max-w-xs">
            <Field label="Signatures offertes à l'ouverture d'un compte" hint="Appliqué aux nouveaux clients en lots prépayés.">
              <input className={inputClass} type="number" min={0} value={trial} onChange={(e) => setTrial(e.target.value)} />
            </Field>
          </div>
        </Card>

        <Card title="Coûts estimés (pour la marge du tableau de bord)">
          <p className="mb-4 text-sm text-slate-600">Estimations de ce que coûte chaque signature à la plateforme. Elles ne servent qu&apos;au calcul de la marge indicative.</p>
          <div className="grid max-w-md grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Coût d'un SMS (€)" hint="0,074 $ ≈ 0,068 €">
              <input className={inputClass} inputMode="decimal" value={sms} onChange={(e) => setSms(e.target.value)} required />
            </Field>
            <Field label="Autres coûts par signature (€)" hint="Stockage, horodatage, e-mails">
              <input className={inputClass} inputMode="decimal" value={signature} onChange={(e) => setSignature(e.target.value)} required />
            </Field>
          </div>
          <div className="mt-6 flex items-center gap-3">
            <Button type="submit" loading={saving}>Enregistrer les réglages</Button>
            {message && <span className={`text-sm ${message.ok ? 'text-emerald-600' : 'text-red-600'}`}>{message.text}</span>}
          </div>
        </Card>
      </form>

      <Card title="Accès à cette administration" className="mt-6">
        <p className="text-sm text-slate-600">
          La connexion se fait avec le mot de passe défini dans la variable <code className="rounded bg-slate-100 px-1">OPERATOR_PASSWORD</code> de l&apos;application API (Coolify). Pour le changer, modifiez
          cette variable puis redéployez l&apos;API. Les sessions durent 8 heures ; cinq échecs bloquent la connexion 15 minutes.
        </p>
      </Card>

      <Card title="Journal des actions" className="mt-6">
        {audit.loading && !audit.data ? (
          <Spinner />
        ) : !audit.data || audit.data.length === 0 ? (
          <Empty>Aucune action enregistrée.</Empty>
        ) : (
          <Table head={['Date', 'Action', 'Détail', 'Adresse IP']}>
            {audit.data.map((entry, index) => (
              <tr key={index}>
                <Td className="whitespace-nowrap">{dateTime(entry.created_at)}</Td>
                <Td className="font-medium text-slate-900">{ACTIONS[entry.action] ?? entry.action}</Td>
                <Td className="max-w-md truncate text-xs text-slate-500">{Object.keys(entry.detail).length ? JSON.stringify(entry.detail) : '—'}</Td>
                <Td className="text-slate-500">{entry.ip_address ?? '—'}</Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
    </div>
  );
}
